import { checkSession } from "@features/auth";
import { useI18nContext } from "@i18n/i18n-react";
import { T } from "@i18n/T";
import { preloadQuery } from "@relay";
import { cn } from "@shadcn/lib/utils";
import { Button } from "@shadcn/ui/button";
import { createFileRoute, Navigate, redirect, useNavigate } from "@tanstack/react-router";
import { Container, Package, PackageCheck } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { usePreloadedQuery } from "react-relay";
import { graphql } from "relay-runtime";
import { AppHeader } from "~/components/layout/AppHeader";
import { PageContainer } from "~/components/layout/PageContainer";
import { ShippingAddress } from "~/components/one-delivery/ShippingAddress";
import type { finishOneDeliveryOnboardingQuery } from "./__generated__/finishOneDeliveryOnboardingQuery.graphql";

const query = graphql`
	query finishOneDeliveryOnboardingQuery {
		onedeliveryProfile {
			...ShippingAddressFragment
		}
	}
`;

export const Route = createFileRoute("/one-delivery/onboarding/finish")({
	beforeLoad: () => {
		document.title = "bacehub | One Delivery";
	},
	component: RouteComponent,
	loader: async ({ context }) => {
		if (!(await checkSession())) {
			throw redirect({ to: "/auth", search: { redirectToPath: "/one-delivery/onboarding/finish", show: "sign-in" } });
		}
		return preloadQuery<finishOneDeliveryOnboardingQuery>(context.relayEnvironment, query, {}, { fetchPolicy: "network-only" });
	},
});

/** Figma "Thank you - Default": the address to use in shop checkouts, with the bace-ID. */
function RouteComponent() {
	const i18n = useI18nContext().LL["new-ui"];
	const navigate = useNavigate();
	const { onedeliveryProfile: profile } = usePreloadedQuery<finishOneDeliveryOnboardingQuery>(query, Route.useLoaderData());
	if (!profile) return <Navigate to="/one-delivery/onboarding/pricing" replace />;
	const next: [LucideIcon, string, string][] = [
		[Package, i18n.OD_ONB_NEXT_1_TITLE(), i18n.OD_ONB_NEXT_1_TEXT()],
		[PackageCheck, i18n.OD_ONB_NEXT_2_TITLE(), i18n.OD_ONB_NEXT_2_TEXT()],
		[Container, i18n.OD_ONB_NEXT_3_TITLE(), i18n.OD_ONB_NEXT_3_TEXT()],
	];

	return (
		<PageContainer
			className="min-h-dvh bg-slate-50"
			footer={
				<Button size="lg" className="w-full font-breit font-normal shadow-xl" onClick={() => navigate({ to: "/one-delivery" })}>
					{i18n.OD_ONB_GO()}
				</Button>
			}
		>
			<AppHeader />
			<div className="flex flex-col gap-1 px-6 pt-4">
				<h1 className="font-breit text-2xl font-normal text-neutral-900">
					<T>{i18n.OD_ONB_DONE_TITLE()}</T>
				</h1>
				<p className="text-base text-neutral-500">{i18n.OD_ONB_DONE_TEXT()}</p>
			</div>
			<div className="px-6">
				<ShippingAddress profile={profile} />
			</div>
			<div className="flex flex-col px-6 pb-8">
				<p className="font-breit text-base leading-none font-medium text-neutral-950">{i18n.OD_ONB_WHAT_NEXT()}</p>
				{next.map(([Icon, title, text], index) => (
					<div key={title} className={cn("flex gap-4 py-5", index > 0 && "border-t border-neutral-200")}>
						<Icon className="size-6 shrink-0" />
						<div className="flex flex-col gap-1">
							<p className="font-breit text-base leading-none font-medium text-neutral-950">{title}</p>
							<p className="text-sm text-neutral-500">{text}</p>
						</div>
					</div>
				))}
			</div>
		</PageContainer>
	);
}
